import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus, Lifebuoy, ChatCircleText, Paperclip, Star, X, Sparkle,
  PaperPlaneTilt, ArrowLeft, DownloadSimple, ThumbsUp, ThumbsDown, CheckCircle
} from '@phosphor-icons/react';

const PRIORITY_COLORS = {
  urgent: 'bg-red-500/15 text-red-400 border-red-500/30',
  high: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  medium: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  low: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
};

const STATUS_COLORS = {
  open: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  in_progress: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  waiting_customer: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  resolved: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  closed: 'bg-slate-500/15 text-slate-400 border-slate-500/30',
};

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'billing', label: 'Billing' },
  { value: 'technical', label: 'Technical' },
  { value: 'account', label: 'Account' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'bug', label: 'Bug Report' },
];

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function shortId(id) { return id ? `#${String(id).slice(-6)}` : ''; }

export default function SupportPage() {
  const { apiCall, user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [active, setActive] = useState(null); // active ticket detail
  const [tab, setTab] = useState('my-tickets');

  useEffect(() => { loadTickets(); }, []);

  async function loadTickets() {
    setLoading(true);
    try {
      const res = await apiCall('get', '/support/tickets');
      setTickets(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto" data-testid="support-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Lifebuoy size={28} className="text-primary" weight="duotone" />
            Support
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Get help from our team or our AI assistant.</p>
        </div>
        <Button onClick={() => setShowNew(true)} data-testid="new-ticket-btn" className="gap-2">
          <Plus size={16} /> New Ticket
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="my-tickets" data-testid="tab-my-tickets">My Tickets</TabsTrigger>
          <TabsTrigger value="kb" data-testid="tab-kb">Knowledge Base</TabsTrigger>
        </TabsList>

        <TabsContent value="my-tickets" className="mt-4">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground text-sm">Loading…</div>
          ) : tickets.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Lifebuoy size={48} className="text-muted-foreground/40 mx-auto mb-3" weight="duotone" />
                <p className="text-muted-foreground mb-4">No tickets yet. We're here when you need us.</p>
                <Button onClick={() => setShowNew(true)} data-testid="empty-new-ticket-btn">Open your first ticket</Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-2">
              {tickets.map(t => (
                <Card
                  key={t.id}
                  className="cursor-pointer hover:border-primary/40 transition-colors"
                  onClick={() => setActive(t)}
                  data-testid={`ticket-row-${t.id}`}
                >
                  <CardContent className="py-4 px-5 flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs text-muted-foreground font-mono">{shortId(t.id)}</span>
                        <Badge variant="outline" className={`text-[10px] ${STATUS_COLORS[t.status] || ''}`}>
                          {t.status?.replace('_', ' ')}
                        </Badge>
                        <Badge variant="outline" className={`text-[10px] ${PRIORITY_COLORS[t.priority] || ''}`}>
                          {t.priority}
                        </Badge>
                      </div>
                      <div className="font-medium truncate">{t.subject}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">Updated {fmtDate(t.updated_at)}</div>
                    </div>
                    {t.satisfaction_rating ? (
                      <div className="flex items-center gap-1 text-amber-400 text-sm">
                        <Star size={14} weight="fill" /> {t.satisfaction_rating}
                      </div>
                    ) : null}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="kb" className="mt-4">
          <KbBrowser apiCall={apiCall} />
        </TabsContent>
      </Tabs>

      <NewTicketDialog
        open={showNew}
        onClose={() => setShowNew(false)}
        apiCall={apiCall}
        user={user}
        onCreated={(t) => { setShowNew(false); loadTickets(); setActive(t); }}
      />

      {active && (
        <TicketDetailDialog
          ticketId={active.id}
          onClose={() => { setActive(null); loadTickets(); }}
          apiCall={apiCall}
          isAdmin={false}
        />
      )}
    </div>
  );
}

// ============ KB BROWSER (tenant) ============
function KbBrowser({ apiCall }) {
  const [articles, setArticles] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);

  async function load() {
    setLoading(true);
    try {
      const res = await apiCall('get', `/support/kb${q ? `?q=${encodeURIComponent(q)}` : ''}`);
      setArticles(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  return (
    <div>
      <Input
        placeholder="Search the knowledge base…"
        value={q}
        onChange={e => setQ(e.target.value)}
        className="mb-4"
        data-testid="kb-search-input"
      />
      {loading ? (
        <div className="text-center py-8 text-muted-foreground text-sm">Loading…</div>
      ) : articles.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground text-sm">
          {q ? 'No articles match your search.' : 'Knowledge base is empty.'}
        </div>
      ) : (
        <div className="space-y-2">
          {articles.map(a => (
            <Card key={a.id} className="cursor-pointer hover:border-primary/40" onClick={() => setOpen(a)} data-testid={`kb-row-${a.id}`}>
              <CardContent className="py-4 px-5">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{a.category}</div>
                <div className="font-medium">{a.title}</div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{open?.title}</DialogTitle>
          </DialogHeader>
          <div className="whitespace-pre-wrap text-sm leading-relaxed py-2">{open?.content}</div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ NEW TICKET DIALOG (with AI first) ============
function NewTicketDialog({ open, onClose, apiCall, onCreated, user }) {
  const [step, setStep] = useState('ai'); // 'ai' | 'form'
  const [aiSession, setAiSession] = useState(null);
  const [aiMsgs, setAiMsgs] = useState([]); // [{role,text}]
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [form, setForm] = useState({ subject: '', category: 'general', priority: 'medium', message: '' });
  const [attachments, setAttachments] = useState([]); // [{id,filename,size}]
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [solved, setSolved] = useState(null); // null | 'pending' | 'yes' | 'no'
  const fileRef = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open) {
      setStep('ai');
      setAiSession(null);
      setAiMsgs([{ role: 'assistant', text: "Hi! I'm RoofMate's AI assistant. Describe your issue and I'll try to help right away. If I can't, you can open a ticket with our team in one click." }]);
      setAiInput('');
      setForm({ subject: '', category: 'general', priority: 'medium', message: '' });
      setAttachments([]);
      setSolved(null);
    }
  }, [open]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [aiMsgs, aiLoading]);

  async function askAi() {
    const q = aiInput.trim();
    if (!q || aiLoading) return;
    setAiInput('');
    setAiMsgs(m => [...m, { role: 'user', text: q }]);
    setAiLoading(true);
    setSolved(null);
    try {
      const res = await apiCall('post', '/support/ai-ask', { question: q, session_id: aiSession });
      setAiSession(res.data.session_id);
      setAiMsgs(m => [...m, { role: 'assistant', text: res.data.answer }]);
    } catch (e) {
      setAiMsgs(m => [...m, { role: 'assistant', text: "I couldn't reach the AI service right now. You can still open a ticket with our team." }]);
    } finally {
      setAiLoading(false);
    }
  }

  async function markSolved(helpful) {
    if (!aiSession) {
      // No session yet — just close
      if (helpful) { toast.success("Glad we could help!"); onClose(); }
      else { setStep('form'); }
      return;
    }
    try {
      await apiCall('post', '/support/ai-feedback', { session_id: aiSession, helpful });
    } catch (e) {
      // AI feedback is best-effort analytics — never block the user flow on failure.
      console.warn('ai-feedback failed:', e);
    }
    if (helpful) {
      setSolved('yes');
      toast.success("Awesome — glad the AI could help! 🎉");
      setTimeout(() => onClose(), 1200);
    } else {
      setSolved('no');
      setStep('form');
    }
  }

  async function handleUpload(e) {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        if (f.size > 5 * 1024 * 1024) {
          toast.error(`${f.name}: exceeds 5 MB limit`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', f);
        const res = await apiCall('post', '/support/attachments', fd);
        setAttachments(a => [...a, res.data]);
      }
    } catch (err) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function handleCreate() {
    if (!form.subject.trim() || !form.message.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        ...form,
        attachment_ids: attachments.map(a => a.id),
        ai_session_id: aiSession || undefined,
      };
      const res = await apiCall('post', '/support/tickets', payload);
      toast.success(`Ticket ${shortId(res.data.id)} created. We'll get back to you soon.`);
      onCreated(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create ticket');
    } finally {
      setCreating(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="new-ticket-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {step === 'ai' ? (
              <>
                <Sparkle size={20} weight="duotone" className="text-primary" />
                Ask our AI first
              </>
            ) : (
              <>
                <ChatCircleText size={20} weight="duotone" className="text-primary" />
                Open a support ticket
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {step === 'ai'
              ? 'Most questions can be answered instantly. If not, you can open a ticket below.'
              : 'A human will respond based on the priority you choose.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'ai' ? (
          <div className="space-y-3">
            <div ref={scrollRef} className="h-72 overflow-y-auto rounded-md border bg-muted/30 p-3 space-y-3" data-testid="ai-chat-window">
              {aiMsgs.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap leading-relaxed ${
                    m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-card border'
                  }`}>
                    {m.text}
                  </div>
                </div>
              ))}
              {aiLoading && (
                <div className="flex justify-start">
                  <div className="bg-card border rounded-lg px-3 py-2 text-sm text-muted-foreground">
                    <span className="inline-block animate-pulse">Thinking…</span>
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                value={aiInput}
                onChange={e => setAiInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); askAi(); } }}
                placeholder="Describe your issue…"
                disabled={aiLoading}
                data-testid="ai-input"
              />
              <Button onClick={askAi} disabled={!aiInput.trim() || aiLoading} data-testid="ai-send-btn">
                <PaperPlaneTilt size={16} />
              </Button>
            </div>

            {/* Did this help? — only after AI has answered at least once and not yet rated */}
            {aiSession && !aiLoading && solved !== 'yes' && aiMsgs[aiMsgs.length - 1]?.role === 'assistant' && aiMsgs.length > 1 && (
              <div className="flex items-center justify-between gap-2 p-3 rounded-md border border-primary/30 bg-primary/5" data-testid="ai-feedback-prompt">
                <div className="text-sm font-medium flex items-center gap-2">
                  <Sparkle size={14} className="text-primary" weight="fill" />
                  Did this solve your question?
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1 border-emerald-500/40 hover:bg-emerald-500/10 hover:text-emerald-300"
                    onClick={() => markSolved(true)}
                    data-testid="ai-helpful-yes-btn"
                  >
                    <ThumbsUp size={14} weight="fill" /> Yes, thanks!
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-1"
                    onClick={() => markSolved(false)}
                    data-testid="ai-helpful-no-btn"
                  >
                    <ThumbsDown size={14} /> No, open a ticket
                  </Button>
                </div>
              </div>
            )}

            {solved === 'yes' && (
              <div className="flex items-center gap-2 p-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-sm" data-testid="ai-solved-banner">
                <CheckCircle size={18} weight="fill" />
                Great! We're logging this as resolved by AI. No ticket created.
              </div>
            )}

            <div className="flex items-center justify-between pt-2">
              <Button variant="ghost" onClick={onClose} data-testid="ai-cancel-btn">Cancel</Button>
              <Button variant="outline" onClick={() => setStep('form')} data-testid="open-ticket-instead-btn">
                Didn't help? Open a ticket →
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label>Subject</Label>
              <Input
                value={form.subject}
                onChange={e => setForm({ ...form, subject: e.target.value })}
                placeholder="Short summary of your issue"
                data-testid="ticket-subject-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="ticket-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="ticket-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low — within 4 days</SelectItem>
                    <SelectItem value="medium">Medium — within 2 days</SelectItem>
                    <SelectItem value="high">High — within 24 hours</SelectItem>
                    <SelectItem value="urgent">Urgent — within 4 hours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Message</Label>
              <Textarea
                rows={6}
                value={form.message}
                onChange={e => setForm({ ...form, message: e.target.value })}
                placeholder="Provide as much detail as possible…"
                data-testid="ticket-message-input"
              />
            </div>
            <div>
              <Label>Attachments (max 5 MB each)</Label>
              <div className="flex flex-wrap items-center gap-2 mt-1">
                {attachments.map(a => (
                  <Badge key={a.id} variant="secondary" className="gap-2 py-1.5 px-2">
                    <Paperclip size={12} /> {a.filename}
                    <button
                      onClick={() => setAttachments(arr => arr.filter(x => x.id !== a.id))}
                      className="hover:text-destructive"
                      data-testid={`remove-attachment-${a.id}`}
                    ><X size={12} /></button>
                  </Badge>
                ))}
                <input ref={fileRef} type="file" multiple onChange={handleUpload} className="hidden" data-testid="attachment-file-input" />
                <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="attach-file-btn">
                  <Paperclip size={14} className="mr-1" /> {uploading ? 'Uploading…' : 'Attach file'}
                </Button>
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button variant="ghost" onClick={() => setStep('ai')} className="gap-1" data-testid="back-to-ai-btn">
                <ArrowLeft size={14} /> Ask AI
              </Button>
              <Button onClick={handleCreate} disabled={creating} data-testid="submit-ticket-btn">
                {creating ? 'Creating…' : 'Submit ticket'}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ============ TICKET DETAIL DIALOG (shared tenant + admin) ============
export function TicketDetailDialog({ ticketId, onClose, apiCall, isAdmin }) {
  const [ticket, setTicket] = useState(null);
  const [replies, setReplies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [internal, setInternal] = useState(false);
  const [sending, setSending] = useState(false);
  const [showRate, setShowRate] = useState(false);
  const [aiDraft, setAiDraft] = useState('');
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [ticketId]);

  async function load() {
    setLoading(true);
    try {
      const res = await apiCall('get', `/support/tickets/${ticketId}`);
      setTicket(res.data.ticket);
      setReplies(res.data.replies || []);
    } catch (e) { toast.error('Failed to load ticket'); onClose(); }
    finally { setLoading(false); }
  }

  async function sendReply() {
    if (!reply.trim() || sending) return;
    setSending(true);
    try {
      await apiCall('post', `/support/tickets/${ticketId}/replies`, { message: reply.trim(), internal });
      setReply(''); setInternal(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to reply');
    } finally { setSending(false); }
  }

  async function updateTicket(patch) {
    try {
      const res = await apiCall('patch', `/support/tickets/${ticketId}`, patch);
      setTicket(res.data);
      toast.success('Ticket updated');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  }

  async function aiSuggest() {
    setAiBusy(true);
    try {
      const res = await apiCall('post', `/support/tickets/${ticketId}/ai-suggest`);
      setAiDraft(res.data.draft || '');
      setReply(res.data.draft || '');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI service error');
    } finally { setAiBusy(false); }
  }

  const canRate = !isAdmin && ticket && ['resolved', 'closed'].includes(ticket.status) && !ticket.satisfaction_rating;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="ticket-detail-dialog">
        {loading || !ticket ? (
          <div className="py-16 text-center text-muted-foreground text-sm">Loading…</div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-xs text-muted-foreground font-mono mb-1">{shortId(ticket.id)} · {ticket.tenant_name}</div>
                  <DialogTitle className="text-lg">{ticket.subject}</DialogTitle>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant="outline" className={STATUS_COLORS[ticket.status]}>{ticket.status?.replace('_', ' ')}</Badge>
                  <Badge variant="outline" className={PRIORITY_COLORS[ticket.priority]}>{ticket.priority}</Badge>
                </div>
              </div>
            </DialogHeader>

            {isAdmin && (
              <div className="grid grid-cols-2 gap-3 pb-3 border-b">
                <Select value={ticket.status} onValueChange={(v) => updateTicket({ status: v })}>
                  <SelectTrigger data-testid="admin-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['open', 'in_progress', 'waiting_customer', 'resolved', 'closed'].map(s =>
                      <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <Select value={ticket.priority} onValueChange={(v) => updateTicket({ priority: v })}>
                  <SelectTrigger data-testid="admin-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {['low', 'medium', 'high', 'urgent'].map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="flex-1 overflow-y-auto py-3 space-y-3">
              {/* Original message */}
              <MessageBubble
                author={ticket.created_by_name || ticket.created_by_email}
                role="tenant"
                date={ticket.created_at}
                message={ticket.description}
                attachmentIds={ticket.attachment_ids}
                apiCall={apiCall}
              />
              {replies.map((r, i) => (
                <MessageBubble
                  key={r.id || i}
                  author={r.author_name || r.author_email}
                  role={r.author_role === 'super_admin' ? 'agent' : 'tenant'}
                  date={r.created_at}
                  message={r.message}
                  internal={r.internal}
                  attachmentIds={r.attachment_ids}
                  apiCall={apiCall}
                />
              ))}
              {ticket.satisfaction_rating && (
                <div className="flex items-center justify-center gap-1 text-amber-400 text-sm pt-2">
                  <Star size={14} weight="fill" /> Customer rated: {ticket.satisfaction_rating}/5
                  {ticket.satisfaction_comment ? <span className="text-muted-foreground ml-2 italic">"{ticket.satisfaction_comment}"</span> : null}
                </div>
              )}
            </div>

            {/* Reply box */}
            {ticket.status !== 'closed' && (
              <div className="border-t pt-3 space-y-2">
                <Textarea
                  rows={3}
                  value={reply}
                  onChange={e => setReply(e.target.value)}
                  placeholder={isAdmin ? "Type your reply to the customer…" : "Add a reply…"}
                  data-testid="reply-textarea"
                />
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    {isAdmin && (
                      <>
                        <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer">
                          <input
                            type="checkbox"
                            checked={internal}
                            onChange={e => setInternal(e.target.checked)}
                            data-testid="internal-note-checkbox"
                          />
                          Internal note (not sent to customer)
                        </label>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={aiSuggest}
                          disabled={aiBusy}
                          className="gap-1"
                          data-testid="ai-suggest-btn"
                        >
                          <Sparkle size={14} />
                          {aiBusy ? 'Drafting…' : 'AI Suggest'}
                        </Button>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {canRate && (
                      <Button size="sm" variant="outline" onClick={() => setShowRate(true)} data-testid="rate-ticket-btn">
                        <Star size={14} className="mr-1" /> Rate
                      </Button>
                    )}
                    <Button onClick={sendReply} disabled={!reply.trim() || sending} size="sm" data-testid="send-reply-btn">
                      {sending ? 'Sending…' : 'Send reply'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Rating dialog */}
            {showRate && (
              <RateDialog
                ticketId={ticketId}
                apiCall={apiCall}
                onClose={() => setShowRate(false)}
                onSuccess={() => { setShowRate(false); load(); }}
              />
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function MessageBubble({ author, role, date, message, internal, attachmentIds, apiCall }) {
  const isAgent = role === 'agent';
  return (
    <div className={`flex ${isAgent ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] rounded-lg px-3 py-2.5 text-sm ${
        internal ? 'bg-amber-500/10 border border-amber-500/30 text-amber-100' :
        isAgent ? 'bg-primary/10 border border-primary/30' : 'bg-card border'
      }`}>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-semibold">{author}</span>
          {internal && <Badge variant="outline" className="text-[9px] bg-amber-500/15 text-amber-300 border-amber-500/30">Internal</Badge>}
          <span className="text-[10px] text-muted-foreground">{fmtDate(date)}</span>
        </div>
        <div className="whitespace-pre-wrap leading-relaxed">{message}</div>
        {Array.isArray(attachmentIds) && attachmentIds.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {attachmentIds.map(aid => (
              <AttachmentLink key={aid} attachmentId={aid} apiCall={apiCall} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function AttachmentLink({ attachmentId, apiCall }) {
  const url = `${process.env.REACT_APP_BACKEND_URL || ''}/api/support/attachments/${attachmentId}`;
  // We use a regular link so the browser handles auth via cookie (backend allows cookie-or-bearer).
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-muted hover:bg-muted/70 border"
      data-testid={`attachment-link-${attachmentId}`}
      onClick={async (e) => {
        // Some browsers won't send cookies for cross-origin, but our backend also supports auth via API.
        // Fallback: fetch via apiCall and open blob.
        try {
          e.preventDefault();
          const res = await apiCall('get', `/support/attachments/${attachmentId}`);
          // If apiCall returned JSON not a file, fall back to opening the URL
          if (typeof res.data === 'string' || res.data instanceof Blob) {
            const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
            const u = URL.createObjectURL(blob);
            window.open(u, '_blank');
          } else {
            window.open(url, '_blank');
          }
        } catch {
          window.open(url, '_blank');
        }
      }}
    >
      <DownloadSimple size={12} /> Attachment
    </a>
  );
}

function RateDialog({ ticketId, apiCall, onClose, onSuccess }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    setSubmitting(true);
    try {
      await apiCall('post', `/support/tickets/${ticketId}/rating`, { rating, comment });
      toast.success('Thanks for your feedback!');
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit rating');
    } finally { setSubmitting(false); }
  }

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>How was your experience?</DialogTitle>
          <DialogDescription>Your feedback helps us improve.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-center gap-2 py-4">
          {[1, 2, 3, 4, 5].map(n => (
            <button
              key={n}
              onClick={() => setRating(n)}
              className={`p-1 transition-transform hover:scale-110 ${n <= rating ? 'text-amber-400' : 'text-muted-foreground/30'}`}
              data-testid={`rate-star-${n}`}
            >
              <Star size={32} weight={n <= rating ? 'fill' : 'regular'} />
            </button>
          ))}
        </div>
        <Textarea
          rows={3}
          value={comment}
          onChange={e => setComment(e.target.value)}
          placeholder="Any feedback to share? (optional)"
          data-testid="rate-comment-input"
        />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} data-testid="submit-rating-btn">
            {submitting ? 'Submitting…' : 'Submit rating'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
